import React, { useState } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { validatePhoneNumber } from '../../utils/validators';

interface PersonalInfoProps {
  profile: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    address?: string;
  };
  onUpdate: (field: string, value: string) => Promise<void>;
}

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 11,
  color: '#7C6FA0',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  fontWeight: 500,
  marginBottom: 3,
};

const VALUE_STYLE: React.CSSProperties = {
  fontSize: 14,
  color: '#1E1148',
  fontWeight: 400,
};

const PersonalInfo: React.FC<PersonalInfoProps> = ({ profile, onUpdate }) => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleEdit = (field: string) => {
    setEditingField(field);
    setEditValue(profile[field as keyof typeof profile] || '');
    setError(null);
  };

  const handleSave = async () => {
    if (!editingField) return;

    if (editingField === 'phone') {
      const validationResult = validatePhoneNumber(editValue);
      if (!validationResult.isValid) {
        setError(validationResult.error);
        return;
      }
    }

    try {
      await onUpdate(editingField, editValue);
      setEditingField(null);
      setError(null);
    } catch {
      setError('Erreur lors de la mise à jour');
    }
  };

  const renderField = (label: string, field: string) => (
    <div
      key={field}
      style={{
        paddingTop: 12,
        paddingBottom: 12,
        borderBottom: '0.5px solid var(--color-border)',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p style={LABEL_STYLE}>{label}</p>
          {editingField === field ? (
            <div className="mt-1">
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                className="w-full px-2 py-1 rounded"
                style={{
                  border: '0.5px solid var(--color-border)',
                  fontSize: 13,
                  color: '#1E1148',
                  outline: 'none',
                }}
                autoFocus
              />
              {error && <p className="mt-1 text-red-600" style={{ fontSize: 11 }}>{error}</p>}
            </div>
          ) : (
            <p style={VALUE_STYLE}>
              {profile[field as keyof typeof profile] || <span style={{ color: '#B8B0CC', fontStyle: 'italic' }}>Non renseigné</span>}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0 mt-1">
          {editingField === field ? (
            <>
              <button
                onClick={handleSave}
                style={{ color: '#059669', background: 'transparent', border: 'none', cursor: 'pointer', padding: 3 }}
                title="Enregistrer"
              >
                <Check size={14} strokeWidth={2} />
              </button>
              <button
                onClick={() => { setEditingField(null); setError(null); }}
                style={{ color: '#7C6FA0', background: 'transparent', border: 'none', cursor: 'pointer', padding: 3 }}
                title="Annuler"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </>
          ) : (
            <button
              onClick={() => handleEdit(field)}
              style={{ color: 'rgb(var(--color-primary))', background: 'transparent', border: 'none', cursor: 'pointer', padding: 3 }}
              title={`Modifier ${label}`}
            >
              <Pencil size={13} strokeWidth={1.75} />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {renderField('Prénom', 'firstName')}
      {renderField('Nom', 'lastName')}
      {renderField('Téléphone', 'phone')}
      {renderField('Adresse', 'address')}
    </div>
  );
};

export default PersonalInfo;
